/**
 * @file client.ts
 * @description GCOME_AP AP 命令客户端，负责排队、重试和帧解析。
 */

import type { HidChannel } from '../../../channels/hid/HidChannel'
import { InputReportExchange } from '../../../channels/hid/exchange/InputReportExchange'
import { GCOME_AP_REPORT } from './cmd'
import { decodeGcomeApFrame, encodeGcomeApFrame } from './frame'
import type { GcomeApRawCommandRequest, GcomeApRawCommandResult } from './types'

export class GcomeApCommandClient {
  constructor(
    private readonly channel: HidChannel,
    private readonly queue: <T>(task: () => Promise<T>) => Promise<T>,
  ) {}

  public async request(request: GcomeApRawCommandRequest): Promise<GcomeApRawCommandResult> {
    return this.queue(() => this.requestWithRetries(request))
  }

  private async requestWithRetries(
    request: GcomeApRawCommandRequest,
  ): Promise<GcomeApRawCommandResult> {
    const maxRetries = request.maxRetries ?? GCOME_AP_REPORT.maxRetries
    let lastError: unknown

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        return await this.requestOnce(request)
      } catch (error) {
        lastError = error
      }
    }

    throw lastError instanceof Error ? lastError : new Error(String(lastError))
  }

  private async requestOnce(request: GcomeApRawCommandRequest): Promise<GcomeApRawCommandResult> {
    const frame = encodeGcomeApFrame(request)
    const reportId = request.reportId ?? GCOME_AP_REPORT.defaultReportId
    const responseReportId = request.responseReportId ?? GCOME_AP_REPORT.defaultResponseReportId
    const exchange = new InputReportExchange(this.channel)
    const responseView = await exchange.request({
      reportId,
      payload: frame.raw,
      responseReportId,
      timeout: request.timeout ?? GCOME_AP_REPORT.timeoutMs,
      validate: (data) => this.isExpectedResponse(data, frame.command),
    })
    const response = decodeGcomeApFrame(responseView)
    return {
      request: frame,
      response,
      reportId,
      responseReportId,
    }
  }

  private isExpectedResponse(data: DataView, command: number): boolean {
    try {
      return decodeGcomeApFrame(data).command === command
    } catch (_error) {
      return false
    }
  }
}
